import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, fetchAccessToken, onSessionExpired, setAccessToken } from '../../lib/api.ts';
import { setDisplayTimeZone } from '../../lib/display.ts';
import type { LoginResponse, User } from '../../lib/types.ts';
import { can, type Role } from '@aq/shared';

interface AuthState {
  user: User | null;
  /** Null until the initial session check finishes, to avoid flashing the login page. */
  ready: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  completeTotp: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-read the account from the server.
   *
   * The user lives in state here rather than in the query cache, so anything
   * that changes it — a new picture, a timezone, enabling two-factor — has to
   * say so explicitly. Invalidating a query would not touch this.
   */
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * One refresh at a time, however many callers ask for it.
 *
 * Refresh tokens rotate: the token presented is spent and a new one issued.
 * Two calls in flight together therefore present the *same* token, the second
 * arrives after the first has spent it, and the server — correctly, on the
 * face of it — reads that as a captured token and revokes the whole family.
 *
 * Which is precisely what happened on every page load. React runs effects
 * twice in development, so mounting fired two refreshes, the second destroyed
 * the session the first had just renewed, and the user was returned to the
 * login screen every time they reloaded. Two tabs opening together would do
 * the same in production.
 *
 * So concurrent callers share one request. What they share is the *token*,
 * not the Response: a body can only be read once, so handing the same
 * Response to two callers leaves the second unable to read it — and under
 * StrictMode the second is the one that survives, which turned a fixed login
 * loop back into a login loop by another route.
 *
 * The promise is held only while the request is in flight; nothing is cached
 * beyond that.
 */
let inFlight: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (inFlight) return inFlight;

  // Waits out a restarting server rather than taking it for a signed-out
  // session; see fetchAccessToken.
  inFlight = fetchAccessToken().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const client = useQueryClient();

  /**
   * Become somebody else — or nobody.
   *
   * Every cached answer in the app was fetched as whoever was signed in a
   * moment ago, and none of the query keys carry their name: `['folders',
   * null]` is *the* Drive root, not *their* Drive root. So signing out and
   * back in as somebody else served the previous account's Drive out of the
   * cache until something refetched it — and with a 30-second staleTime that
   * is not a flicker, it is half a minute of the wrong Drive. Both ways
   * round: a sub-user granted one file was shown the whole Drive, and the
   * Owner who checked on them afterwards was shown the one file.
   *
   * The server was never fooled — every one of those rows would have been
   * refused on open — but a listing is names and paths, and those are the
   * half of a private folder that matters.
   *
   * Cleared synchronously, before the new user is set, rather than in an
   * effect afterwards. An effect runs after the first render of the new
   * session, and that render is one frame of somebody else's Drive.
   *
   * Every path that changes who is signed in goes through here, including the
   * ones that do not exist yet: a passkey login added later cannot forget to
   * do this, because there is nowhere else to set the user.
   */
  const becomes = useCallback(
    (next: User | null) => {
      client.clear();
      setUser(next);
    },
    [client],
  );

  /**
   * Restore a session on load.
   *
   * The access token lives in memory and is gone after a refresh of the page,
   * but the httpOnly refresh cookie survives — so one refresh call silently
   * re-establishes the session without the user seeing a login screen.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const accessToken = await refreshOnce();
        if (!accessToken) return;
        setAccessToken(accessToken);
        const me = await api.get<User>('/api/auth/me');
        if (!cancelled) becomes(me);
      } catch {
        // No usable session. The login page is the correct outcome.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [becomes]);

  useEffect(() => {
    onSessionExpired(() => {
      setAccessToken(null);
      becomes(null);
    });
  }, [becomes]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<LoginResponse>('/api/auth/login', { email, password });
      if (result.status === 'authenticated') {
        setAccessToken(result.accessToken);
        becomes(result.user);
      }
      return result;
    },
    [becomes],
  );

  const completeTotp = useCallback(
    async (challengeToken: string, code: string) => {
      const result = await api.post<Extract<LoginResponse, { status: 'authenticated' }>>(
        '/api/auth/totp',
        { challengeToken, code },
      );
      setAccessToken(result.accessToken);
      becomes(result.user);
    },
    [becomes],
  );

  const reload = useCallback(async () => {
    try {
      setUser(await api.get<User>('/api/auth/me'));
    } catch {
      // A failure here means the session went away, and the 401 handler has
      // already dealt with that. Leaving the stale user is the safer outcome
      // than blanking the screen on a transient network blip.
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setAccessToken(null);
      becomes(null);
    }
  }, [becomes]);

  /*
   * Every time shown in the product is shown in the account's zone.
   *
   * Set here because this is the one place that knows who is looking, and it
   * covers the whole product at once: the Drive's dates, the audit trail, the
   * save indicator, the clock in the header. Before this the setting was asked
   * for on first run, stored, and then ignored by every formatter — a Dubai
   * business read an hour off the moment somebody opened it from Karachi.
   */
  useEffect(() => {
    setDisplayTimeZone(user?.timeZone);
  }, [user?.timeZone]);

  const value = useMemo(
    () => ({ user, ready, login, completeTotp, logout, reload }),
    [user, ready, login, completeTotp, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/**
 * The page to land on after signing in, and the fallback for an unknown URL.
 *
 * It used to be the dashboard for everybody, which was true while every role
 * could read one. A Viewer cannot: the client asked for an account that sees
 * the Drive and nothing else about the system, so the dashboard is now its own
 * permission — and sending them there on sign-in would greet them with a
 * refusal on the first screen of the product.
 *
 * The Drive is the honest fallback. It is what a Viewer is here for, and it is
 * where anyone else would go next anyway.
 */
export function landingFor(role: Role | undefined): string {
  return role && can(role, 'dashboard:read') ? '/dashboard' : '/drive';
}
